import React, { useEffect, useContext } from 'react';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { detectIE } from '../../../helpers/browserHelpers';
import { ListerContext } from '../listerContext';
import listerAppStyles from '../listerApp.module.pcss';

function fixFacetsMenuTop(facetsMenuRef: React.RefObject<HTMLDivElement>): void {
  if (facetsMenuRef.current) {
    facetsMenuRef.current.style.marginTop = '0';
    facetsMenuRef.current.style.top = '0';
    facetsMenuRef.current.style.bottom = 'unset';
    facetsMenuRef.current.style.alignSelf = 'flex-start';
  }
}

function fixFacetsMenuBottom(facetsMenuRef: React.RefObject<HTMLDivElement>): void {
  if (facetsMenuRef.current) {
    facetsMenuRef.current.style.marginTop = '0';
    facetsMenuRef.current.style.top = 'unset';
    facetsMenuRef.current.style.bottom = '0';
    facetsMenuRef.current.style.alignSelf = 'flex-end';
  }
}

function isFixableFacetsMenu(): boolean {
  const facetsMenuElement = document.getElementById('facets-menu');
  const productsElement = document.getElementById('products');
  return facetsMenuElement && productsElement ? facetsMenuElement.offsetHeight < productsElement.offsetHeight : false;
}

function isFacetsMenuFixed(facetsMenuRef: React.RefObject<HTMLDivElement>): boolean | null {
  return (
    facetsMenuRef.current && facetsMenuRef.current.classList.contains(listerAppStyles['lister__facets-menu--fixed'])
  );
}

function useFixFacetsMenu(): {
  facetsRef: React.RefObject<HTMLElement>;
  facetsMenuRef: React.RefObject<HTMLDivElement>;
  listingRef: React.RefObject<HTMLElement>;
} {
  const [{ isFacetsDisplayed, isListerDisabled }] = useContext(ListerContext);
  const { isSmall } = useMediaQuery();
  const facetsRef = React.useRef<HTMLElement>(null);
  const facetsMenuRef = React.useRef<HTMLDivElement>(null);
  const listingRef = React.useRef<HTMLElement>(null);

  useEffect(() => {
    if (!isListerDisabled && isFacetsMenuFixed(facetsMenuRef)) {
      window.scrollTo(0, 0);
    }
  }, [isListerDisabled]);

  useEffect(() => {
    let lastScroll = document.documentElement.scrollTop;
    let lastDelta = 0;
    let frozenPosition = false;

    const handleFacetsStickyBehavior = (): void => {
      if (
        isFacetsDisplayed &&
        facetsRef.current &&
        facetsMenuRef.current &&
        listingRef.current &&
        isFixableFacetsMenu()
      ) {
        const facetsRect = facetsRef.current.getBoundingClientRect();
        const facetsMenuRect = facetsMenuRef.current.getBoundingClientRect();
        const delta = document.documentElement.scrollTop - lastScroll;

        if (detectIE()) {
          if (facetsRect.top < 0 && facetsRect.bottom > window.innerHeight) {
            facetsMenuRef.current.style.position = `fixed`;
            facetsMenuRef.current.style.top = `0`;
            facetsMenuRef.current.style.marginTop = `0`;
          } else if (facetsRect.bottom <= window.innerHeight) {
            facetsMenuRef.current.style.position = `static`;
            facetsMenuRef.current.style.marginTop = `${facetsRect.height - facetsMenuRect.height}px`;
          } else {
            facetsMenuRef.current.style.position = `static`;
            fixFacetsMenuTop(facetsMenuRef);
          }
        } else {
          /**
           * Apply top in the bottom of the lister
           */
          if (facetsRect.bottom < window.innerHeight) {
            fixFacetsMenuTop(facetsMenuRef);
            frozenPosition = false;
          }

          /**
           * Replace margin with sticky when needed
           */
          if (frozenPosition) {
            if (facetsRect.top > 0) {
              fixFacetsMenuBottom(facetsMenuRef);
              frozenPosition = false;
            } else if (facetsRect.bottom < 0) {
              fixFacetsMenuTop(facetsMenuRef);
              frozenPosition = false;
            } else if (window.innerHeight >= facetsMenuRect.top + facetsMenuRect.height) {
              fixFacetsMenuBottom(facetsMenuRef);
              frozenPosition = false;
            } else if (facetsMenuRect.top > 0) {
              fixFacetsMenuTop(facetsMenuRef);
              frozenPosition = false;
            }
          }

          /**
           * Freeze position on scroll direction change
           */
          if ((delta > 0 && lastDelta < 0) || (delta < 0 && lastDelta > 0)) {
            facetsMenuRef.current.style.marginTop = `${facetsMenuRect.top - facetsRect.top}px`;
            facetsMenuRef.current.style.top = 'unset';
            frozenPosition = true;
          }

          lastScroll = document.documentElement.scrollTop;
          lastDelta = delta;
        }
      }
    };

    if (!isSmall) {
      window.addEventListener('scroll', handleFacetsStickyBehavior);
    }

    return (): void => {
      window.removeEventListener('scroll', handleFacetsStickyBehavior);
    };
  });

  return { facetsRef, facetsMenuRef, listingRef };
}

export default useFixFacetsMenu;
